import Phaser from 'phaser';
import {CardConfig, CardSettings, getCardSpriteName} from './CardTypes';
import {Stats} from './Stats';
import { Room } from './Room';
import {applyCardEffect} from "./CardEffect";
import GrayScalePostFx from "phaser3-rex-plugins/plugins/grayscalepipeline.js";

export class Card {
    private scene: Phaser.Scene;
    private config: CardConfig;
    private sprite: Phaser.GameObjects.Sprite;
    private outline: Phaser.GameObjects.Graphics;
    private isHovered: boolean = false;
    private onUse: (card: Card) => void;
    private isBeingUsed: boolean = false;
    private isLocked: boolean = false;

    private drawOutline(x: number, y: number): void {
        this.outline.clear();
        this.outline.lineStyle(2, 0x00ff00);
        this.outline.strokeRect(
            x - CardSettings.width / 2,
            y - CardSettings.height / 2,
            CardSettings.width,
            CardSettings.height
        );
    }

    constructor(scene: Phaser.Scene, x: number, y: number, config: CardConfig, onUse: (card: Card) => void) {
        this.scene = scene;
        this.config = config;
        this.onUse = onUse;

        const roundedX = Math.round(x);
        const roundedY = Math.round(y);

        const spriteName = getCardSpriteName(config)

        this.sprite = scene.add.sprite(roundedX, roundedY, spriteName);
        this.sprite.setDisplaySize(CardSettings.width, CardSettings.height);
        this.sprite.setInteractive();
        this.sprite.setDepth(1000);

        this.outline = scene.add.graphics();
        this.drawOutline(roundedX, roundedY);
        this.outline.setVisible(false);
        this.outline.setDepth(1001);

        this.setupHoverEffect();
        this.setupClickHandler();
    }

    private disableHoverEffect(): void {
        this.isHovered = false;
        this.outline.setVisible(false);
    }

    private setupHoverEffect(): void {
        this.sprite.on('pointerover', () => {
            if (this.isBeingUsed || this.isLocked) return;
            this.isHovered = true;
            this.outline.setVisible(true);
        });

        this.sprite.on('pointerout', () => {
            if (this.isLocked) return;
            this.isHovered = false;
            this.outline.setVisible(false);
        });
    }

    private setupClickHandler(): void {
        this.sprite.on('pointerdown', () => {
            if (this.isBeingUsed || this.isLocked) return;
            this.onUse(this);
        });
    }

    public getSprite(): Phaser.GameObjects.Sprite {
        return this.sprite;
    }

    public getOutline(): Phaser.GameObjects.Graphics {
        return this.outline;
    }

    public getConfig(): CardConfig {
        return this.config;
    }

    public destroy(): void {
        this.sprite.destroy();
        this.outline.destroy();
    }

    public updatePosition(x: number, y: number): void {
        const roundedX = Math.round(x);
        const roundedY = Math.round(y);

        this.sprite.setPosition(roundedX, roundedY);

        this.drawOutline(roundedX, roundedY);
    }

    public applyEffect(stats: Stats, currentRoom: Room): void {
        this.isBeingUsed = true;
        this.sprite.setAlpha(0.5);
        this.outline.setVisible(false);

        applyCardEffect(this.scene, this.config, stats, currentRoom)
    }

    public lock(): void {
        this.isLocked = true;
        this.disableHoverEffect();
        this.sprite.setAlpha(0.75)
        this.sprite.setPostPipeline("GrayScale")
    }

    public unlock(): void {
        this.isLocked = false;
        this.sprite.setAlpha(1.0)
        this.sprite.removePostPipeline("GrayScale")
    }
}
